import * as h3 from 'h3-js';

/**
 * Clamps a numeric value to a safe range for protobuf varint encoding.
 * Protobuf varints have a practical limit that can cause "Given varint doesn't fit into 10 bytes" errors
 * when values are extremely large. We use Number.MAX_SAFE_INTEGER as a conservative upper bound.
 * @param {number} value - Value to clamp
 * @returns {number} Clamped value within safe range
 */
/**
 * Checks if two numbers are effectively equal using tolerance-based comparison.
 * Handles floating point precision issues by using both absolute and relative comparisons.
 * @param {number} a - First value
 * @param {number} b - Second value
 * @param {number} epsilon - Tolerance for comparison (default: 1e-10)
 * @returns {boolean} True if values are effectively equal
 */
export const areValuesEffectivelyEqual = (a, b, epsilon = 1e-10) => {
  if (a === b) return true; // Fast path for exact equality
  if (typeof a !== 'number' || typeof b !== 'number' || !isFinite(a) || !isFinite(b)) {
    return false;
  }
  const diff = Math.abs(b - a);
  const maxAbs = Math.max(Math.abs(a), Math.abs(b));
  // Use absolute comparison for near-zero values, relative for larger values
  return diff < epsilon || (maxAbs > epsilon && diff / maxAbs < epsilon);
};

const clampToSafeRange = (value) => {
  if (typeof value !== 'number' || !isFinite(value)) {
    return value;
  }
  // Use Number.MAX_SAFE_INTEGER (2^53 - 1) as the safe upper bound for protobuf encoding
  // This prevents varint encoding errors in MapLibre GL JS
  const MAX_SAFE = Number.MAX_SAFE_INTEGER;
  const MIN_SAFE = -Number.MAX_SAFE_INTEGER;

  if (value > MAX_SAFE) {
    console.warn(`Value ${value} exceeds safe range, clamping to ${MAX_SAFE}`);
    return MAX_SAFE;
  }
  if (value < MIN_SAFE) {
    console.warn(`Value ${value} exceeds safe range, clamping to ${MIN_SAFE}`);
    return MIN_SAFE;
  }
  return value;
};

/**
 * Converts a list of H3 cell data into a GeoJSON FeatureCollection.
 * @param {Array} items - List of objects with { h3: string, value: number, ... }
 * @returns {Object} GeoJSON FeatureCollection
 */
export const h3ToGeoJSON = (items) => {
  const features = items.map(item => {
    try {
      // Get the vertices of the H3 cell
      const boundary = h3.cellToBoundary(item.h3, true); // true for [lng, lat]

      // h3-js returns [[lng, lat], ...] for GeoJSON-friendly format
      // We need to close the loop: last point = first point
      const coordinates = [...boundary, boundary[0]];

      // Clamp value to safe range to prevent protobuf varint encoding errors
      const safeValue = typeof item.value === 'number' ? clampToSafeRange(item.value) : item.value;

      return {
        type: 'Feature',
        properties: {
          ...item,
          // We include h3 and value explicitly for style functions
          h3: item.h3,
          value: safeValue
        },
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        }
      };
    } catch (e) {
      console.warn(`Invalid H3 index: ${item.h3}`, e);
      return null;
    }
  }).filter(f => f !== null);

  return {
    type: 'FeatureCollection',
    features
  };
};

/**
 * Converts items with pre-computed geometry to GeoJSON FeatureCollection.
 * Used for polygon/point queries where geometry is already in the response.
 * @param {Array} items - List of objects with { geometry: object, value: number, ... }
 * @returns {Object} GeoJSON FeatureCollection
 */
export const itemsToGeoJSON = (items) => {
  const features = items.map(item => {
    // If geometry is already an object, use it directly
    const geometry = item.geometry;

    if (!geometry) {
      console.warn('Item missing geometry field:', item);
      return null;
    }

    // Create feature with all properties except geometry
    const { geometry: _, ...properties } = item;

    // Clamp value to safe range to prevent protobuf varint encoding errors
    const safeValue = typeof item.value === 'number' ? clampToSafeRange(item.value) : item.value;

    return {
      type: 'Feature',
      properties: {
        ...properties,
        value: safeValue // Ensure value is included for styling, clamped to safe range
      },
      geometry: geometry
    };
  }).filter(f => f !== null);

  return {
    type: 'FeatureCollection',
    features
  };
};

/**
 * Computes equal-count quantiles from actual data values
 * Each quantile contains approximately the same number of data points
 * @param {Object} layer - Layer object with data (GeoJSON) and stats
 * @param {number} numStops - Number of quantile stops (default: 6)
 * @returns {Array} Array of quantile values
 */
export const computeEqualCountQuantiles = (layer, numStops = 6) => {
  // Extract all valid numeric values from the layer data
  const values = extractLayerValues(layer);

  // If no values or insufficient data, fall back to stats-based quantiles
  if (values.length === 0) {
    const min = layer.stats?.min ?? 0;
    const max = layer.stats?.max ?? 100;
    const step = (max - min) / (numStops - 1);
    return Array.from({ length: numStops }, (_, i) => min + i * step);
  }

  // Sort values in ascending order
  const sortedValues = [...values].sort((a, b) => a - b);
  const n = sortedValues.length;

  // For numStops quantile stops, we create numStops-1 bins with equal counts
  // Each bin should contain approximately n / (numStops - 1) values
  const quantiles = [];

  // Always include the minimum value as the first quantile
  quantiles.push(sortedValues[0]);

  // Compute intermediate quantiles that divide data into equal-count bins
  // For i from 1 to numStops-2, calculate the index that divides the data
  // into equal-sized groups
  for (let i = 1; i < numStops - 1; i++) {
    // Calculate the index: i * n / (numStops - 1)
    // This divides the sorted array into equal-count segments
    const index = Math.floor((i * n) / (numStops - 1));
    // Clamp to valid range
    const clampedIndex = Math.min(Math.max(0, index), n - 1);
    quantiles.push(sortedValues[clampedIndex]);
  }

  // Always include the maximum value as the last quantile
  quantiles.push(sortedValues[n - 1]);

  return quantiles;
};

/**
 * Gets all values from layer GeoJSON data
 * @param {Object} layer - Layer object with data (GeoJSON)
 * @returns {Array} Array of numeric values
 */
export const extractLayerValues = (layer) => {
  if (!layer.data || !layer.data.features) {
    return [];
  }

  return layer.data.features
    .map(feature => feature.properties?.value)
    .filter(val => typeof val === 'number' && !isNaN(val) && isFinite(val));
};

/**
 * Gets unique values from layer GeoJSON data
 * @param {Object} layer - Layer object with data (GeoJSON)
 * @returns {Array} Array of unique numeric values, sorted
 */
export const extractUniqueValues = (layer) => {
  const values = extractLayerValues(layer);
  return [...new Set(values)].sort((a, b) => a - b);
};

/**
 * Computes discrete color bands for a layer
 * Returns the exact same color bands used in both map rendering and legend
 * @param {Object} layer - Layer object with stats and config
 * @param {Function} getPaletteColors - Function to get palette colors
 * @param {Function} mapIntervalToColorIndex - Function to map interval to color index
 * @param {Function} getDefaultPalette - Function to get default palette name
 * @param {number} numStops - Number of quantile stops (default: 6)
 * @returns {Object} Object with { quantiles, colorBands, stepExpression }
 */
export const computeDiscreteColorBands = (layer, getPaletteColors, mapIntervalToColorIndex, getDefaultPalette, numStops = 6) => {
  const min = layer.stats?.min ?? 0;
  const max = layer.stats?.max ?? 100;

  const paletteName = layer.config?.palette || getDefaultPalette();
  const isInverted = layer.config?.inverted || false;

  // Compute quantiles and ensure they're sorted and unique
  let quantiles = computeEqualCountQuantiles(layer, numStops);
  quantiles = [...new Set(quantiles)].sort((a, b) => a - b);

  // Get palette colors
  const paletteColors = getPaletteColors(paletteName, isInverted);
  if (!paletteColors || paletteColors.length === 0) {
    // Fallback to default palette
    const defaultColors = getPaletteColors(getDefaultPalette(), false);
    const fallbackColor = defaultColors && defaultColors.length > 0 ? defaultColors[0] : '#440154';
    return {
      quantiles,
      colorBands: [],
      stepExpression: null,
      useLinear: true,
      minColor: fallbackColor,
      maxColor: fallbackColor
    };
  }

  // Handle edge case: when all values are the same (min === max)
  // Use a single constant color for all features
  // Use tolerance-based comparison for floating point numbers to handle precision issues
  if (areValuesEffectivelyEqual(min, max)) {
    // Use the middle color from the palette (or first if only one color)
    const colorIndex = Math.floor(paletteColors.length / 2);
    const constantColor = paletteColors[colorIndex];

    // Create a single color band for the legend
    const edgeCaseBands = [{
      value: min,
      nextValue: max,
      color: constantColor,
      leftPercent: 0,
      widthPercent: 100
    }];

    // Return a constant color expression (not linear interpolation)
    // This is more efficient and clearer than linear interpolation with same min/max
    return {
      quantiles: [min, max],
      colorBands: edgeCaseBands,
      stepExpression: constantColor, // Constant color for all features
      useLinear: false, // Not using linear interpolation, using constant color
      minColor: constantColor,
      maxColor: constantColor,
      isCategorical: false
    };
  }

  // Check if we should use categorical legend (fewer than 10 unique values)
  const uniqueValues = extractUniqueValues(layer);
  if (uniqueValues.length > 0 && uniqueValues.length < 10) {
    // Create categorical mapping: each unique value gets its own color
    const categories = uniqueValues.map((value, index) => {
      // Distribute colors evenly across the palette
      // Handle case when there's only one value (shouldn't happen due to min === max check above, but be safe)
      const colorIndex = uniqueValues.length === 1
        ? Math.floor(paletteColors.length / 2)
        : Math.floor((index / (uniqueValues.length - 1)) * (paletteColors.length - 1));
      const clampedColorIndex = Math.min(colorIndex, paletteColors.length - 1);
      return {
        value: value,
        color: paletteColors[clampedColorIndex]
      };
    });

    // Create step expression for categorical mapping
    // Use a step expression that maps each unique value to its color
    const categoricalStepExpression = ['step', ['get', 'value']];

    // Default color (for values not in our list)
    categoricalStepExpression.push(paletteColors[0]);

    // Add stops for each category value (clamp to safe range)
    categories.forEach(category => {
      const safeCategoryValue = typeof category.value === 'number' ? clampToSafeRange(category.value) : category.value;
      categoricalStepExpression.push(safeCategoryValue);
      categoricalStepExpression.push(category.color);
    });

    // Create categorical color bands for legend
    const categoricalBands = categories.map(category => ({
      value: category.value,
      nextValue: category.value, // Same value for categorical
      color: category.color,
      leftPercent: 0, // Not used for categorical
      widthPercent: 0 // Not used for categorical
    }));

    return {
      quantiles: uniqueValues,
      colorBands: categoricalBands,
      stepExpression: categoricalStepExpression,
      useLinear: false,
      minColor: null,
      maxColor: null,
      isCategorical: true,
      categories: categories
    };
  }

  // Handle edge case: if quantiles are invalid (but min !== max), use linear interpolation
  // But still create color bands for the legend
  if (quantiles.length < 2) {
    const minColor = paletteColors[0];
    const maxColor = paletteColors[paletteColors.length - 1];

    // Create a single color band for the legend
    const edgeCaseBands = [{
      value: min,
      nextValue: max,
      color: minColor,
      leftPercent: 0,
      widthPercent: 100
    }];

    return {
      quantiles: [min, max],
      colorBands: edgeCaseBands,
      stepExpression: null,
      useLinear: true,
      minColor,
      maxColor,
      isCategorical: false
    };
  }

  // Compute discrete color bands
  const numIntervals = quantiles.length - 1;
  const colorBands = [];
  const stepExpression = ['step', ['get', 'value']];

  // Get default color for values below first quantile
  const firstColorIndex = mapIntervalToColorIndex(0, numIntervals, paletteColors.length);
  const firstColor = paletteColors[firstColorIndex];
  stepExpression.push(firstColor);

  // Calculate position and width for colorbar using linear scale
  // Since we're using equal-count quantiles, we use linear positioning based on min/max
  const range = max - min;

  // Create color bands and step expression stops
  for (let i = 0; i < numIntervals; i++) {
    const quantileValue = quantiles[i];
    const nextValue = quantiles[i + 1];

    // Skip if quantile value is invalid
    if (typeof quantileValue !== 'number' || isNaN(quantileValue) || !isFinite(quantileValue)) {
      continue;
    }

    // Skip if nextValue is invalid (required for color band calculation)
    if (typeof nextValue !== 'number' || isNaN(nextValue) || !isFinite(nextValue)) {
      continue;
    }

    // Clamp quantile values to safe range to prevent protobuf varint encoding errors
    // These values are used in MapLibre step expressions and must be within safe bounds
    const safeQuantileValue = clampToSafeRange(quantileValue);

    // Map interval index to palette color index
    const colorIndex = mapIntervalToColorIndex(i, numIntervals, paletteColors.length);
    const bandColor = paletteColors[colorIndex];

    // Add to step expression with clamped values
    stepExpression.push(safeQuantileValue);
    stepExpression.push(bandColor);

    // Use linear positioning for colorbar (use original values for display, not clamped)
    const leftPercent = range > 0 ? ((quantileValue - min) / range) * 100 : 0;
    const widthPercent = range > 0 ? ((nextValue - quantileValue) / range) * 100 : 100 / numIntervals;

    colorBands.push({
      value: quantileValue, // Keep original value for display
      nextValue: nextValue, // Keep original value for display
      color: bandColor,
      leftPercent: Math.max(0, Math.min(100, leftPercent)),
      widthPercent: Math.max(1, Math.min(100, widthPercent))
    });
  }

  return {
    quantiles,
    colorBands,
    stepExpression,
    useLinear: false,
    minColor: null,
    maxColor: null,
    isCategorical: false
  };
};

/**
 * Format numbers for display with simplified abbreviations (K, M, B)
 * Used consistently across legend and hover tooltips
 * @param {number|null|undefined} val - Value to format
 * @returns {string} Formatted value string
 */
export const formatValue = (val) => {
  if (val === null || val === undefined) return 'N/A';

  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';

  // Simplify large numbers with abbreviations
  if (absVal >= 1e9) {
    return `${sign}${(absVal / 1e9).toFixed(4)} B`;
  } else if (absVal >= 1e6) {
    return `${sign}${(absVal / 1e6).toFixed(4)} M`;
  } else if (absVal >= 1) {
    return `${sign}${absVal.toFixed(1)}`;
  } else if (absVal >= 0.01) {
    return `${sign}${absVal.toFixed(4)}`;
  } else {
    // For very small numbers, use scientific notation
    return `${sign}${absVal.toExponential(1)}`;
  }
};
